import { Box, Stack, Typography } from '@mui/material';
import React from 'react';

import { colors } from '../../theme/theme';
import { getSupportStatusStyles, highlightSupportWord, parseSupportRequests } from '../MessageBubbleHelpers';

export const SupportRequestsCard: React.FC<{
    supportRequests: ReturnType<typeof parseSupportRequests>;
}> = ({ supportRequests }) => {
    if (!supportRequests) return null;
    const supportTitle = (supportRequests.intro || 'Support Requests').replace(/:\s*$/, '').trim();
    return (
        <Box>
            <Typography
                variant="body2"
                sx={{ mb: 1, color: colors.greyPreset[50], fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.35 }}
            >
                {highlightSupportWord(supportTitle)}
            </Typography>

            <Stack gap={1}>
                {supportRequests.requests.map(request => {
                    const statusStyles = getSupportStatusStyles(request.status);
                    const normalizedRequestType = request.type && request.type !== '-' ? request.type : '-';
                    return (
                        <Box
                            key={request.ticketNumber}
                            sx={{
                                border: `1px solid ${colors.greyPreset[20]}`,
                                borderRadius: '12px',
                                backgroundColor: colors.fullWhite,
                                boxShadow: '0 2px 10px rgba(16, 24, 40, 0.06)',
                                px: 1,
                                py: 0.9,
                            }}
                        >
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                gap={1}
                                sx={{ mb: 0.85 }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{ color: colors.greyPreset[50], fontWeight: 700, fontSize: '0.8rem' }}
                                >
                                    #{request.ticketNumber}
                                </Typography>
                                {request.hasStatus && (
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            px: 0.8,
                                            py: 0.15,
                                            borderRadius: '6px',
                                            backgroundColor: statusStyles.bg,
                                            color: statusStyles.color,
                                            fontWeight: 700,
                                            fontSize: '0.78rem',
                                            letterSpacing: 0.2,
                                        }}
                                    >
                                        {request.status}
                                    </Typography>
                                )}
                            </Stack>

                            <Stack
                                direction="row"
                                gap={1.25}
                                alignItems="stretch"
                                sx={{ borderTop: `1px solid ${colors.greyPreset[20]}`, pt: 0.8 }}
                            >
                                <Box sx={{ minWidth: '102px' }}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: colors.greyPreset[50],
                                            fontWeight: 700,
                                            display: 'block',
                                            lineHeight: 1.2,
                                            textTransform: 'uppercase',
                                        }}
                                    >
                                        Date
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: colors.fullBlack,
                                            fontWeight: 600,
                                            fontSize: '0.9rem',
                                            lineHeight: 1.35,
                                        }}
                                    >
                                        {request.date || '-'}
                                    </Typography>
                                </Box>

                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: colors.greyPreset[50],
                                            fontWeight: 700,
                                            display: 'block',
                                            lineHeight: 1.2,
                                            textTransform: 'uppercase',
                                        }}
                                    >
                                        Request Type
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: colors.fullBlack,
                                            fontWeight: 600,
                                            fontSize: '0.9rem',
                                            lineHeight: 1.35,
                                        }}
                                    >
                                        {normalizedRequestType}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>
                    );
                })}
            </Stack>

            {supportRequests.followUp && (
                <Typography variant="body2" sx={{ mt: 0.9, color: colors.greyPreset[60], lineHeight: 1.55 }}>
                    {highlightSupportWord(supportRequests.followUp)}
                </Typography>
            )}
        </Box>
    );
};
